import React from 'react';
import {
  AccessTime,
  Person,
  Assignment,
  AttachFile,
} from '@mui/icons-material';
import { Incident, IncidentStatus } from '@models/incident.model';
import { Badge } from '@components/atoms/Badge';
import Alert from '@components/atoms/Alert';
import { Modal } from '@components/molecules/Modal';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { useTranslation } from '@hooks/useTranslation';

interface AcknowledgeIncidentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  incident: Incident | null;
  isAcknowledging: boolean;
}

const AcknowledgeIncidentModal: React.FC<AcknowledgeIncidentModalProps> = ({
  open,
  onClose,
  onConfirm,
  incident,
  isAcknowledging,
}) => {
  const tActivityCards = useTranslation('activityCards');
  const tCommon = useTranslation('common');
  const tIncidents = useTranslation('incidents');

  // Sprint 28 change request (sixth-site defect fix, orchestrator-confirmed). Reuses the
  // already-shipped `incidents.type*`/`incidents.unit*` keys (built for ReportIncidentModal)
  // instead of a second set of keys for the same concepts. Unmapped values fall back to the raw
  // value, never `undefined`.
  const incidentTypeTextMap: Record<string, string> = {
    WEATHER: tIncidents.typeWeather,
    ACCIDENT: tIncidents.typeAccident,
    MATERIALS_ISSUE: tIncidents.typeMaterialsIssue,
    EQUIPMENT_ISSUE: tIncidents.typeEquipmentIssue,
    OTHER: tIncidents.typeOther,
  };
  const delayUnitTextMap: Record<string, string> = {
    HOURS: tIncidents.unitHours,
    DAYS: tIncidents.unitDays,
    WEEKS: tIncidents.unitWeeks,
  };

  // AM/PM are left untranslated on purpose — this app's Spanish catalog otherwise keeps
  // clock-time notation as-is (no existing precedent translates it), and house style
  // (docs/ux/i18n-house-style.md) doesn't call out time-of-day abbreviations as prose needing
  // translation.
  const formatTime = (dateInput: Date | string): string => {
    const date = new Date(dateInput);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const formatDate = (dateInput: Date | string): string => {
    const date = new Date(dateInput);
    const today = new Date();

    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      return tActivityCards.incident.todayAtPrefix.replace(
        '{time}',
        formatTime(dateInput),
      );
    }

    const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    return tActivityCards.incident.dateAtTimeFormat
      .replace('{date}', dateStr)
      .replace('{time}', formatTime(dateInput));
  };

  const getIncidentTypeDisplay = (
    type?: string,
    otherType?: string,
  ): string => {
    if (type === 'OTHER' && otherType) {
      return otherType;
    }
    if (!type) return tActivityCards.incident.unknownTypeFallback;
    return incidentTypeTextMap[type] || tActivityCards.incident.unknownTypeFallback;
  };

  if (!incident) return null;

  const isPending = incident.status === IncidentStatus.PENDING;
  const modalTitle = tActivityCards.incident.incidentDetailsTitle;

  // Sprint 28 change request (story-144 History, fifth-site defect fix). `IncidentStatus` only
  // has these two values (backend/src/entities/incident/incident.entity.ts) — mirrors the map
  // already shipped in `ActivityIncident`/`ActivityIncidentCard` for the same enum.
  const incidentStatusTextMap: Record<string, string> = {
    PENDING: tActivityCards.incident.statusPending,
    ACKNOWLEDGED: tActivityCards.incident.statusAcknowledged,
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={modalTitle}
      size="large"
      showCloseButton={true}
      closeOnEscape={true}
      closeOnOverlayClick={true}
      cancelText={isPending ? tCommon.cancel : tCommon.close}
      confirmText={
        isPending
          ? isAcknowledging
            ? tActivityCards.incident.acknowledgingButton
            : tActivityCards.incident.acknowledgeIncidentButton
          : undefined
      }
      handleConfirm={isPending ? onConfirm : undefined}
      isLoading={isAcknowledging}
      disableConfirm={isAcknowledging}>
      {/* Modal Header with Icon */}
      <div className="flex items-center gap-2 mb-4">
        <ReportProblemRoundedIcon
          className={`text-2xl ${isPending ? 'text-amber-500' : 'text-gray-400'}`}
        />
        <h2 className="text-xl font-semibold text-gray-900">
          {incident.name}
        </h2>
      </div>

      {/* Incident Header */}
      <Alert
        variant={isPending ? 'error' : 'info'}
        border
        className="p-5">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 flex-1">
            {incident.name}
          </h3>
          <div className="flex gap-2">
            <Badge variant={isPending ? 'warning' : 'success'} size="sm">
              {incidentStatusTextMap[incident.status] || incident.status}
            </Badge>
            {incident.incidentType && (
              <Badge variant="secondary" size="sm">
                {getIncidentTypeDisplay(
                  incident.incidentType,
                  incident.otherIncidentType,
                )}
              </Badge>
            )}
          </div>
        </div>

        {incident.description && (
          <p className="text-gray-700 leading-relaxed">
            {incident.description}
          </p>
        )}
      </Alert>

      {/* Incident Details */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900 mt-4">
          {tActivityCards.incident.detailsSectionLabel}
        </h3>
        <div className="flex flex-col gap-4">
          {incident.incidentTime && (
            <div className="flex items-center gap-3">
              <AccessTime className="text-lg text-gray-500" />
              <p className="text-sm text-gray-700">
                {tActivityCards.incident.timeOfIncident.replace(
                  '{time}',
                  formatDate(incident.incidentTime),
                )}
              </p>
            </div>
          )}

          {incident.delay != null && incident.delay > 0 && (
            <div className="flex items-center gap-3">
              <AccessTime className="text-lg text-gray-500" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">{tActivityCards.incident.delayLabel}</span>{' '}
                {incident.delay}{' '}
                {(incident.delayUnitOfMeasure &&
                  delayUnitTextMap[incident.delayUnitOfMeasure]) ||
                  incident.delayUnitOfMeasure?.toLowerCase()}
              </p>
            </div>
          )}

          {incident.tasks && incident.tasks.length > 0 && (
            <div className="flex items-start gap-3">
              <Assignment className="text-lg text-gray-500 mt-0.5" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">{tActivityCards.incident.affectedTasksLabel}</span>{' '}
                {incident.tasks.map((task) => task.name).join(', ')}
              </p>
            </div>
          )}

          {incident.project && (
            <div className="flex items-center gap-3">
              <Assignment className="text-lg text-gray-500" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">{tActivityCards.projectLabel}</span>{' '}
                {incident.project.name}
              </p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Person className="text-lg text-gray-500" />
            <p className="text-sm text-gray-700">
              <span className="font-medium">{tActivityCards.incident.reportedByLabel}</span>{' '}
              {incident.user?.firstName} {incident.user?.lastName}
            </p>
          </div>

          {/* Acknowledged by info (only for acknowledged incidents) */}
          {!isPending && incident.acknowledgedByUser && (
            <div className="flex items-center gap-3">
              <Person className="text-lg text-green-500" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">{tActivityCards.incident.acknowledgedByLabel}</span>{' '}
                {incident.acknowledgedByUser.firstName}{' '}
                {incident.acknowledgedByUser.lastName}
                {incident.acknowledgedAt &&
                  ` ${tActivityCards.incident.acknowledgedOnPrefix.replace('{date}', formatDate(incident.acknowledgedAt))}`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Documents Section */}
      {incident.documents && incident.documents.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-900 mt-4">
            {tActivityCards.incident.attachments.replace(
              '{n}',
              String(incident.documents.length),
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {incident.documents.map((document, index) => {
              const isImage = document.name?.match(
                /\.(jpg|jpeg|png|gif|webp)$/i,
              );
              return (
                <div
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(document.url, '_blank');
                  }}
                  className="border border-gray-200 rounded-lg p-3 cursor-pointer transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                  <div className="flex items-center gap-2 mb-2">
                    <AttachFile className="text-base text-gray-500" />
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {document.name ||
                        `${tActivityCards.incident.documentFallback} ${index + 1}`}
                    </p>
                  </div>
                  {isImage && (
                    <div className="w-full h-30 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img
                        src={document.url}
                        alt={document.name}
                        className="max-w-full max-h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Acknowledgment Warning - Only for PENDING incidents */}
      {isPending && (
        <>
          <div className="border-t border-gray-200 mt-4" />
          <Alert variant="warning" border className="p-4 mt-4">
            <h4 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
              <ReportProblemRoundedIcon className="text-lg" />{' '}
              {tActivityCards.incident.acknowledgeConfirmTitle}
            </h4>
            <p className="text-sm text-amber-700 leading-relaxed">
              {tActivityCards.incident.acknowledgeConfirmBody}
            </p>
          </Alert>
        </>
      )}
    </Modal>
  );
};

export default AcknowledgeIncidentModal;
